import './search-info.scss';
import React, {Fragment, useCallback} from 'react';
import {Button} from 'react-bootstrap';
import {
    useStateLastSearchedQuery,
    useStateResultFor,
    useStateSpellCheckResult,
    useStateTotalFound,
} from '../../search-hooks';
import {useDispatch} from 'react-redux';
import {fetchFacets, search} from '../../duck/search-actions';
import {useTranslation} from 'react-i18next';
import {NavigateFunction, useNavigate} from 'react-router-dom';
import {useLocalizationUtil} from '../../../localizationUtil';
import {clearAllSelectedFilters} from '../../../facets/duck/filter.actions';
import {useAreFiltersPersistent} from '../../../facets/duck/filter.hooks';

export const SearchInfo = React.memo(() => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate: NavigateFunction = useNavigate();

    const lastSearchedQuery = useStateLastSearchedQuery();
    const spellCheckResult = useStateSpellCheckResult();
    const totalFound = useStateTotalFound() || 0;
    const resultFor = useStateResultFor();
    const isPersistent: boolean = useAreFiltersPersistent();
    const {formatNumber} = useLocalizationUtil();


    const triggerSearch = useCallback((searchExpression: string) => {
            if (!isPersistent) {
                dispatch(clearAllSelectedFilters());
            }
            dispatch(search(navigate, searchExpression, undefined, false));
            dispatch(fetchFacets(searchExpression, false));
        },
        [dispatch, isPersistent, navigate]
    );

    const didYouMeanCmp = () => {
        let text: string, link: string;
        // if there are any resultsFor (means, a first search was triggered) create the results for link
        if (resultFor) {
            text = t('search.label.info.resultsFor');
            link = resultFor;

            // otherwise the "instead search for" link was clicked and a search without spellcheck suggestions was triggered before.
            // -> create the standard "did you mean" link
        } else {
            text = t('search.label.info.didYouMean');
            link = spellCheckResult;
        }
        return (
            <>
                <span>|</span>
                <div className="did-you-mean">
                    {text}:
                    <Button
                        variant="link"
                        className="did-you-mean-link"
                        onClick={() => triggerSearch(spellCheckResult || '')}
                    >
                        {link}
                    </Button>
                </div>
            </>
        );
    };

    const insteadSearchForCmp = () => {
        if (resultFor) {
            return (
                <>
                    <span>|</span>
                    <div id="instead-search-for" className="did-you-mean">
                        {t('search.label.info.insteadSearchFor')}:
                        <Button
                            variant="link"
                            className="did-you-mean-link"
                            onClick={() => triggerSearch(lastSearchedQuery)}
                        >
                            {lastSearchedQuery}
                        </Button>
                    </div>
                </>
            );
        }
    };

    const renderTotalDocumentsFound = () => {
        return (
            <span id={'total-products-found'}>
            {`${formatNumber(totalFound)} ${t('search.label.info.result_plural')}`}
            </span>
        );
    };

    return (
        <Fragment>
            <div id="search-info">
                {renderTotalDocumentsFound()}
                {spellCheckResult && ( // if having any results display box, otherwise nothing
                    <div className="search-suggestions-box">
                        {didYouMeanCmp()}
                        {insteadSearchForCmp()}
                    </div>
                )}
            </div>
        </Fragment>
    );
});
